import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TMessage, useMessageManyV2 } from 'ui-model';
import { ChatActivityReason } from 'requests/resources';

import { ContentLoaderChatMessage } from 'components/v2';
import { Message } from '../Message';
import { MessageDetails } from '../MessageDetails';
import { Thread } from '../Thread';
import messages from './DcdThread.messages';

export interface DcdThreadProps {
  patientId?: string;
  externalId?: string;
  chatActivityReasons?: ChatActivityReason[];
  retriedChannels: { name: string; reason: string }[];
  originalMessage: TMessage;
}

export const DcdThread: React.FC<DcdThreadProps> = ({ originalMessage, externalId, patientId, chatActivityReasons, retriedChannels }) => {
  const { formatMessage } = useIntl();
  const { data: messagesPages, isLoading } = useMessageManyV2({
    patient: patientId,
    'externalId.value': externalId,
    _populate: 'facility,patient,createdBy',
    _sort: 'createdAt',
  }, { enabled: !!externalId && !!patientId });
  const dcdMessages = useMemo(() => messagesPages?.pages.flat() || [], [messagesPages?.pages]);

  if (!externalId || dcdMessages.length === 0) {
    return (
      <Message
        message={originalMessage}
        chatActivityReasons={chatActivityReasons}
        retriedChannels={retriedChannels}
      />
    );
  }

  if (isLoading) return <ContentLoaderChatMessage toRight={false} />;

  const isSingleMessage = dcdMessages?.length === 1;

  if (isSingleMessage && dcdMessages[0]?.media?.contentType !== 'mp3') {
    return (
      <>
        {dcdMessages.map((message) => (
          <Message
            key={message._id}
            message={message}
            chatActivityReasons={chatActivityReasons}
            retriedChannels={retriedChannels}
          />
        ))}
      </>
    );
  }

  const patient = dcdMessages[0]?.patient;

  return (
    <Thread
      initialMessage={formatMessage(messages.threadMessage, { name: patient.name })}
      interlocutor={patient}
      MessageDetailsComponent={
        <MessageDetails
          isThreadOpener
          retriedChannels={retriedChannels}
          variant="captionBold"
          color="primary"
          message={dcdMessages[0]}
        />
      }
    >
      {dcdMessages.map((message) => (
        <Message
          key={message._id}
          message={message}
          chatActivityReasons={chatActivityReasons}
          retriedChannels={retriedChannels}
        />
      ))}
    </Thread>
  );
};

export default DcdThread;
